import {
  selectFullPaymentPlansData,
  selectSelectedPaymentPlanId,
} from '@checkout-b/checkout/selectors/paymentPlans';
import { CurrencyContext } from '@checkout-c/CurrencyContext';
import { getJSFormattedCurrency } from '@checkout-h/currency';
import Translation from '@checkout-s/Translation';
import clsx from 'clsx';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Fragment } from 'react/jsx-runtime';
import { PaymentPlanModal } from './PaymentPlanModal';
import { setPaymentPlan } from '@checkout-b/checkout/actions/paymentPlans';
import { PAYMENT_PLAN_GATEWAY_TYPES } from '@showclix-shared/Payment/constants/gatewayTypes';
import { getConfigBooleanValue } from '@showclix-shared-util/configClient';

const defaultPaymentOption = {
  paymentPlanId: -1,
  payInFull: true,
};

const PaymentPlanOption = ({
  paymentPlanId,
  numberOfInstallments,
  initialInstallmentAmount = {},
  selected,
  currencyConfig,
  payInFull = false,
  disabled = false,
  amountPending = false,
  onOpenDetails,
  onSelect,
}) => {
  const optionDisabled = disabled || amountPending;

  return (
    <div className="input__radio payment-plan-option" aria-busy={amountPending}>
      <div className="input__radio-wrap">
        <div className="input__radio__input">
          <input
            name="payment"
            type="radio"
            id={`payment-plan-${paymentPlanId}`}
            value={paymentPlanId}
            onChange={onSelect}
            checked={selected}
            disabled={optionDisabled}
          />
        </div>
        <label
          id={`label-payment-plan-${paymentPlanId}`}
          className="input__radio__label"
          htmlFor={`payment-plan-${paymentPlanId}`}
        >
          <div className="input__radio__label-text">
            {payInFull && <Translation>{'checkout__pay_in_full'}</Translation>}
            {!payInFull && (
              <Fragment>
                <p className="payment-plan-option__title">
                  <Translation
                    interpolation={{
                      count: numberOfInstallments,
                    }}
                  >
                    {'checkout__pay_in_installments'}
                  </Translation>
                </p>
                <p className="payment-plan-option__description">
                  {amountPending && <Translation>{'checkout__loading'}</Translation>}
                  {!amountPending && (
                    <Fragment>
                      <Translation
                        interpolation={{
                          amount: getJSFormattedCurrency(initialInstallmentAmount.amount, currencyConfig),
                        }}
                      >
                        {'checkout__pay_in_installments_description'}
                      </Translation>{' '}
                      <button
                        className="payment-plan-option__details-button button--link"
                        type="button"
                        onClick={onOpenDetails}
                      >
                        <Translation>{'checkout__pay_in_installments_full_details'}</Translation>
                      </button>
                    </Fragment>
                  )}
                </p>
              </Fragment>
            )}
          </div>
        </label>
      </div>
    </div>
  );
};

export const PaymentPlans = () => {
  const dispatch = useDispatch();

  const currencyConfig = useContext(CurrencyContext);
  const paymentPlansEnabled = getConfigBooleanValue('paymentPlans', false);

  const gatewayType = useSelector((state) => state.payment.gatewayType);
  const paymentPlans = useSelector(selectFullPaymentPlansData);
  const selectedPaymentPlanId = useSelector(selectSelectedPaymentPlanId);
  // When Stripe BNPL (Afterpay/Klarna/etc) is selected, plans are mutually exclusive.
  const bnplSelectedInStripe = useSelector((state) => !!state.payment.bnplSelectedInStripe);
  const isValidGatewayType = PAYMENT_PLAN_GATEWAY_TYPES.includes(Number(gatewayType));

  // Snap any selected plan back to "Pay in Full" the moment BNPL becomes active.
  useEffect(() => {
    if (bnplSelectedInStripe && selectedPaymentPlanId !== defaultPaymentOption.paymentPlanId) {
      dispatch(setPaymentPlan(defaultPaymentOption.paymentPlanId));
    }
  }, [bnplSelectedInStripe, selectedPaymentPlanId, dispatch]);

  const [paymentPlanIdOpened, setPaymentPlanIdOpened] = useState(null);

  const openedPaymentPlan = useMemo(() => {
    return paymentPlans.find((plan) => plan.paymentPlanId === paymentPlanIdOpened);
  }, [paymentPlanIdOpened, paymentPlans]);

  if (!paymentPlansEnabled) {
    return null;
  }

  const onOpenDetails = (paymentPlanId) => () => {
    setPaymentPlanIdOpened(paymentPlanId);
  };

  const onCloseDetails = () => {
    setPaymentPlanIdOpened(null);
  };

  const onPaymentPlanSelect = (paymentPlanId) => () => {
    dispatch(setPaymentPlan(paymentPlanId));
  };

  if (!paymentPlans.length || !isValidGatewayType) {
    return null;
  }

  return (
    <section
      className={clsx('checkout-form__section payment-options', {
        'payment-options--disabled': bnplSelectedInStripe,
      })}
      aria-disabled={bnplSelectedInStripe}
    >
      <div className="form-card">
        <div className="form-card__header">
          <h3 className="form-card__heading">
            <Translation>{'checkout__payment_options'}</Translation>
          </h3>
        </div>
        <div className="form-card__content">
          {[defaultPaymentOption, ...paymentPlans].map((plan) => (
            <PaymentPlanOption
              key={plan.paymentPlanId}
              {...plan}
              currencyConfig={currencyConfig}
              onOpenDetails={onOpenDetails(plan.paymentPlanId)}
              onSelect={onPaymentPlanSelect(plan.paymentPlanId)}
              selected={selectedPaymentPlanId === plan.paymentPlanId}
              disabled={bnplSelectedInStripe}
              amountPending={!plan.payInFull && plan.initialInstallmentAmount?.amount == null}
            />
          ))}
          {bnplSelectedInStripe && (
            <p className="payment-options__notice">
              <Translation>{'checkout__payment_plan_unavailable_with_bnpl'}</Translation>
            </p>
          )}
        </div>
        <PaymentPlanModal
          open={!!openedPaymentPlan}
          onClose={onCloseDetails}
          currencyConfig={currencyConfig}
          {...openedPaymentPlan}
        />
      </div>
    </section>
  );
};
